/**
 * Umsatz-Tab des Owner-Dashboards.
 *
 * Der Umsatz ist die Summe der Monatspreise aller am Monatsende aktiven
 * Mitglieder – gestapelt nach Standort, damit Gesamthöhe und Anteil in einem
 * Diagramm lesbar sind. Die bisherige Tabelle bleibt als Tabellenansicht erhalten.
 */

import React from 'react';
import { ChartCard, ColumnChart, BarList, StatTile, StatGrid, seriesColor } from './charts/Charts';
import './OwnerStats.css';

/** "September 2026" -> "Sep 26" (Achsenbeschriftung muss schmal sein) */
const shortMonth = (label) => {
  const [name, year] = String(label).split(' ');
  if (!year) return label;
  return `${name.slice(0, 3)} ${year.slice(-2)}`;
};

const OwnerRevenue = ({ dashboard, studios = [], formatCurrency }) => {
  if (!dashboard?.revenue) return null;
  const { revenue } = dashboard;
  const table = revenue.monthly_table || [];

  const money = (v) => (formatCurrency ? formatCurrency(v) : `${Math.round(v || 0)} €`);
  /** Achsen brauchen kurze Zahlen, keine ausgeschriebene Währung. */
  const axisMoney = (v) => new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 }).format(v || 0);

  const series = studios.map((s, i) => ({
    id: s.id,
    name: s.name,
    color: seriesColor(i),
    values: table.map((row) => row[s.name] || 0),
  }));

  const first = table.length ? table[0].total : 0;
  const last = table.length ? table[table.length - 1].total : 0;
  const growth = first > 0 ? Math.round(((last - first) / first) * 100) : null;

  return (
    <div className="owner-section stats-section">
      <div className="stats-head">
        <div>
          <h2>Umsatz</h2>
          <p className="stats-head-sub">
            Wiederkehrender Monatsumsatz aus laufenden Mitgliedschaften.
          </p>
        </div>
      </div>

      <StatGrid>
        <StatTile
          hero
          label="Monatlicher Gesamtumsatz"
          value={money(revenue.monthly_total)}
          trend={table.map((r) => r.total)}
          delta={growth == null ? null : { value: growth, text: `${Math.abs(growth)} %`, label: `seit ${table[0]?.month || ''}`, goodWhen: 'up' }}
        />
        <StatTile label="Ø pro Mitglied" value={money(revenue.avg_per_customer)} />
        {revenue.per_studio.map((s) => (
          <StatTile
            key={s.studio_id}
            label={s.studio_name}
            value={money(s.monthly_revenue)}
            sub={`${s.member_count} Mitglieder · Ø ${money(s.avg_price)}`}
          />
        ))}
      </StatGrid>

      <ChartCard
        title="Umsatzentwicklung"
        subtitle="Letzte 6 Monate, gestapelt nach Standort"
        table={{
          columns: ['Monat', ...studios.map((s) => s.name), 'Gesamt'],
          rows: table.map((row) => [
            row.month,
            ...studios.map((s) => money(row[s.name] || 0)),
            money(row.total),
          ]),
        }}
      >
        <ColumnChart
          categories={table.map((r) => shortMonth(r.month))}
          series={series}
          stacked
          height={280}
          formatValue={axisMoney}
          ariaLabel="Monatsumsatz je Standort, gestapelt"
        />
      </ChartCard>

      <ChartCard title="Umsatz je Standort" subtitle="Aktueller Monat">
        <BarList
          items={revenue.per_studio.map((s) => ({ label: s.studio_name, value: s.monthly_revenue }))}
          formatValue={money}
          labelWidth={150}
          valueWidth={96}
        />
      </ChartCard>
    </div>
  );
};

export default OwnerRevenue;
